import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientiApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Building2, User, CreditCard, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const ClienteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCliente = async () => {
      try {
        const response = await clientiApi.getOne(id);
        setCliente(response.data);
      } catch (error) {
        toast.error('Errore nel caricamento');
        navigate('/clienti');
      } finally {
        setLoading(false);
      }
    };
    fetchCliente();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await clientiApi.update(id, cliente);
      toast.success('Cliente salvato con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => setCliente({ ...cliente, [field]: value });

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!cliente) return null;

  return (
    <div className="p-6 md:p-10" data-testid="cliente-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clienti')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 tracking-tight">{cliente.denominazione_sociale}</h1>
            <p className="text-slate-500 text-sm">{cliente.comune}{cliente.provincia ? `, ${cliente.provincia}` : ''}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-cliente-btn" className="bg-primary hover:bg-primary/90">
          <Save size={18} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </div>

      <div className="space-y-6">
        <Card data-testid="dati-aziendali-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']"><Building2 size={20} className="text-primary" />Dati Aziendali</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="denominazione">Denominazione Sociale</Label>
              <Input id="denominazione" value={cliente.denominazione_sociale} onChange={(e) => handleChange('denominazione_sociale', e.target.value)} data-testid="input-denominazione-sociale" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="indirizzo">Indirizzo</Label>
              <Input id="indirizzo" value={cliente.indirizzo || ''} onChange={(e) => handleChange('indirizzo', e.target.value)} placeholder="Via Roma, 1" data-testid="input-indirizzo" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="comune">Comune</Label>
                <Input id="comune" value={cliente.comune || ''} onChange={(e) => handleChange('comune', e.target.value)} data-testid="input-comune" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provincia">Provincia</Label>
                <Input id="provincia" value={cliente.provincia || ''} onChange={(e) => handleChange('provincia', e.target.value)} data-testid="input-provincia" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email"><Mail size={14} className="inline mr-1" />Email</Label>
              <Input id="email" type="email" value={cliente.email || ''} onChange={(e) => handleChange('email', e.target.value)} placeholder="info@azienda.it" data-testid="input-email" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="dati-fiscali-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']"><CreditCard size={20} className="text-primary" />Dati Fiscali</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
              <Input id="codice_fiscale" value={cliente.codice_fiscale || ''} onChange={(e) => handleChange('codice_fiscale', e.target.value)} data-testid="input-codice-fiscale" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="partita_iva">Partita IVA</Label>
              <Input id="partita_iva" value={cliente.partita_iva || ''} onChange={(e) => handleChange('partita_iva', e.target.value)} data-testid="input-partita-iva" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="referenti-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']"><User size={20} className="text-primary" />Referenti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium text-sm flex items-center gap-2"><Phone size={14} className="text-slate-500" />Referente 1</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ref1_nome">Nome</Label>
                  <Input id="ref1_nome" value={cliente.referente1_nome || ''} onChange={(e) => handleChange('referente1_nome', e.target.value)} placeholder="Mario Rossi" data-testid="input-referente1-nome" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref1_tel">Telefono</Label>
                  <Input id="ref1_tel" value={cliente.referente1_telefono || ''} onChange={(e) => handleChange('referente1_telefono', e.target.value)} placeholder="+39 333 1234567" data-testid="input-referente1-telefono" />
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium text-sm flex items-center gap-2"><Phone size={14} className="text-slate-500" />Referente 2</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ref2_nome">Nome</Label>
                  <Input id="ref2_nome" value={cliente.referente2_nome || ''} onChange={(e) => handleChange('referente2_nome', e.target.value)} placeholder="Luca Bianchi" data-testid="input-referente2-nome" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref2_tel">Telefono</Label>
                  <Input id="ref2_tel" value={cliente.referente2_telefono || ''} onChange={(e) => handleChange('referente2_telefono', e.target.value)} placeholder="+39 333 7654321" data-testid="input-referente2-telefono" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="note-card">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Note</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea value={cliente.note || ''} onChange={(e) => handleChange('note', e.target.value)} placeholder="Aggiungi note sul cliente..." rows={4} data-testid="input-note" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClienteDetailPage;
